import { getCookieValue, getToken } from "@/utils/cookie";
import { ElMessage } from "element-plus";
import { BUSINESS_ERROR_CODES, ERROR_MESSAGE_MAP } from "./common";
import { getDeviceHeaders } from "./deviceInfo";

// 响应数据接口
export interface ApiResponse<T = any> {
  data: T;
  status?: {
    code: number;
    msg: string;
    request_id: string;
  };
}

// 请求配置接口
export interface RequestConfig {
  /** 重试次数 */
  retryCount?: number;
  /** 请求超时时间(ms) */
  timeout?: number;
  /** 额外的请求头 */
  headers?: Record<string, string>;
  /** 请求参数（用于GET请求） */
  params?: Record<string, any>;
  /** 取消请求的 AbortSignal */
  signal?: AbortSignal;
  /** 为 true 时不弹出全局错误提示（如埋点、推广上报） */
  silent?: boolean;
}

// HTTP 方法类型
export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

// 请求拦截器类型
export interface RequestInterceptor {
  onRequest?: (config: any) => any;
  onRequestError?: (error: any) => void;
  onResponse?: (response: any) => any;
  onResponseError?: (error: any) => void;
}

// 统一请求配置接口
export interface UnifiedRequestConfig extends RequestConfig {
  /** HTTP 请求方法 */
  method: HttpMethod;
  /** 请求URL */
  url: string;
  /** 请求数据 */
  data?: any;
}

// API 错误接口
export interface ApiError {
  code: number;
  message: string;
  originalError?: any;
}

// 默认配置
const defaultConfig: RequestConfig = {
  retryCount: 0,
  timeout: 60000,
};

/**
 * 获取 API 基础 URL
 * 开发环境走代理（使用相对路径），生产环境使用完整的后端域名
 */
const getApiBaseUrl = (): string => {
  // 开发模式下使用相对路径，走 Vite/Nitro 代理
  if (import.meta.dev) {
    return "";
  }

  // 生产环境使用 runtimeConfig 中配置的后端域名
  const config = useRuntimeConfig();
  return (config.public.PROXY_TARGET as string) || "";
};

/**
 * axios 风格的请求类
 */
class AxiosLikeRequest {
  private defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };

  /**
   * 获取当前语言
   * 将内部语言代码转换为标准的 HTTP Accept-Language 格式
   */
  private getCurrentLanguage(): string {
    let locale = "en";

    if (import.meta.client) {
      const nuxtApp = useNuxtApp();
      const { $i18n } = nuxtApp || {};
      locale = ($i18n as any)?.locale?.value || "en";
    }

    // 转换语言代码为标准格式
    switch (locale) {
      case "zh":
      case "zhCN":
        return "zh-CN";
      case "zhTW":
        return "zh-TW";
      default:
        return locale;
    }
  }

  /**
   * 显示错误消息
   * 优先匹配 HTTP 状态码，其次判断是否为网络连接错误或超时
   */
  private showErrorMessage(error: any) {
    if (!import.meta.client || typeof window === "undefined") return;

    // 用户主动取消请求，不弹提示
    if (error?.name === "AbortError") return;

    const statusCode = error?.statusCode || error?.status;

    const isTimeoutError =
      !statusCode &&
      (error?.name === "TimeoutError" ||
        error?.cause?.name === "TimeoutError" ||
        error?.message?.includes("timeout"));

    const isNetworkError =
      !statusCode &&
      !isTimeoutError &&
      (error instanceof TypeError ||
        error?.name === "FetchError" ||
        error?.name === "TypeError" ||
        error?.cause instanceof TypeError ||
        error?.cause?.name === "TypeError" ||
        statusCode === 0);

    const errorMessage = isTimeoutError
      ? ERROR_MESSAGE_MAP["ERR_TIMEOUT"]
      : isNetworkError
        ? ERROR_MESSAGE_MAP["ERR_NETWORK"]
        : ERROR_MESSAGE_MAP[statusCode];

    if (errorMessage) {
      ElMessage.error(errorMessage);
    }
  }

  /**
   * 核心请求方法
   */
  async request<T = any>(
    config: {
      url: string;
      method?: string;
      data?: any;
      params?: Record<string, any>;
      headers?: Record<string, string>;
    } & RequestConfig,
  ): Promise<ApiResponse<T>> {
    const mergedConfig = { ...defaultConfig, ...config };
    const {
      url,
      method = "GET",
      data,
      params,
      headers = {},
      retryCount,
      timeout,
      signal,
      silent,
    } = mergedConfig;

    // 获取 token 用于 Authorization 头
    const token = import.meta.client ? getToken() : "";

    // 构建请求头
    const requestHeaders: Record<string, string> = {
      ...this.defaultHeaders,
      ...headers,
      "seame-Language": this.getCurrentLanguage(),
      "x-ad-id": getCookieValue("adid") || "",
      ...getDeviceHeaders(),
    };

    // 客户端添加 Authorization 头
    if (token) {
      requestHeaders["Authorization"] = token;
    }
    // 构建请求配置
    const fetchOptions: any = {
      method: method.toUpperCase(),
      headers: requestHeaders,
      timeout,
      retry: retryCount,
      signal,
    };

    // 服务端渲染时透传请求头信息
    if (import.meta.server) {
      const event = useRequestEvent();
      if (event?.node?.req?.headers) {
        const incomingHeaders = event.node.req.headers;
        const additionalHeaders: Record<string, any> = {};

        // 透传 Cookie（如果存在）
        if (incomingHeaders.cookie) {
          additionalHeaders.cookie = incomingHeaders.cookie;

          // 从 cookie 中提取 token 设置到 Authorization 头
          const tokenMatch = incomingHeaders.cookie.match(/B2BToken=([^;]+)/);
          if (tokenMatch && tokenMatch[1]) {
            fetchOptions.headers["Authorization"] = decodeURIComponent(
              tokenMatch[1],
            );
          }
        }

        // 透传灰度标识
        if (incomingHeaders["x-gray-pre"]) {
          additionalHeaders["X-Gray-Pre"] = true;
        }

        // 透传客户端真实 IP
        if (incomingHeaders["x-forwarded-for"]) {
          additionalHeaders["x-forwarded-for"] =
            incomingHeaders["x-forwarded-for"];
        }

        // 确保 deviceId 正确传递（优先从 nuxtApp 上下文读取）
        try {
          const nuxtApp = useNuxtApp();
          const deviceId = nuxtApp.$deviceId as string;
          if (deviceId) {
            fetchOptions.headers["seame-device-id"] = deviceId;
            console.log("从 nuxtApp 上下文设置 deviceId 到请求头:", deviceId);
          }
        } catch {
          // 如果从上下文读取失败，尝试从 cookie 中提取
          if (incomingHeaders.cookie) {
            const deviceIdMatch =
              incomingHeaders.cookie.match(/deviceId=([^;]+)/);
            if (deviceIdMatch && deviceIdMatch[1]) {
              fetchOptions.headers["seame-device-id"] = deviceIdMatch[1];
              console.log(
                "从 cookie 提取 deviceId 到请求头:",
                deviceIdMatch[1],
              );
            }
          }
        }

        // 合并到请求头中，保留之前的预设头
        fetchOptions.headers = {
          ...fetchOptions.headers,
          ...additionalHeaders,
        };
      }
    }

    // 处理请求数据
    if (data) {
      if (data instanceof FormData && !headers["Content-Type"]) {
        delete fetchOptions.headers["Content-Type"];
      }
      fetchOptions.body = data;
    }

    // 获取 API 基础 URL 并构建完整请求地址
    const baseUrl = getApiBaseUrl();
    let finalUrl = baseUrl ? `${baseUrl}${url}` : url;

    // 处理查询参数
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      finalUrl += `${finalUrl.includes("?") ? "&" : "?"}${searchParams.toString()}`;
    }

    try {
      const response = await $fetch<ApiResponse<T>>(finalUrl, fetchOptions);

      // 处理业务错误
      if (
        response &&
        typeof response === "object" &&
        response?.status &&
        typeof response?.status === "object" &&
        "code" in response.status
      ) {
        const { code } = response?.status || {};
        if (code !== 10000 && code !== undefined) {
          // 业务错误码白名单 - 特殊处理的错误码
          if (BUSINESS_ERROR_CODES.has(code)) {
            if ([10401, 100002, 100001, 10002].includes(code)) {
              if (import.meta.client && typeof window !== "undefined") {
                try {
                  const { useUserStore } = await import("@/stores/user");
                  const userStore = useUserStore();
                  userStore.logout();
                } catch (error) {
                  console.error("用户登出失败:", error);
                }
                throw response.status;
              } else {
                console.log("response.status", response.status);
                throw response.status;
              }
            } else {
              console.log(
                "对于所有业务错误码（包括未登录用户的情况），都需要显示错误提示",
                response.status,
              );
              // 对于所有业务错误码（包括未登录用户的情况），都需要显示错误提示
              throw response.status;
            }
          } else {
            // 直接抛出接口返回的错误状态
            throw response.status;
          }
        }
      }

      return response;
    } catch (error: any) {
      console.error("API Error:", error);
      if (!silent) {
        this.showErrorMessage(error);
      }
      // 抛出格式化的错误
      const apiError: ApiError = {
        code: error?.code || error?.status || error?.statusCode || -1,
        message: error?.msg || "请求失败",
        originalError: error,
      };
      throw apiError;
    }
  }

  /**
   * GET 请求
   */
  get<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: "GET", ...config });
  }

  /**
   * POST 请求
   */
  post<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: "POST", data, ...config });
  }

  /**
   * PUT 请求
   */
  put<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: "PUT", data, ...config });
  }

  /**
   * DELETE 请求
   */
  delete<T = any>(
    url: string,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: "DELETE", ...config });
  }

  /**
   * PATCH 请求
   */
  patch<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig,
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: "PATCH", data, ...config });
  }
}

// 创建默认实例
const axiosInstance = new AxiosLikeRequest();

/**
 * 统一的HTTP请求方法
 * @param config 统一请求配置
 * @returns Promise<ApiResponse<T>>
 * @example
 * // GET 请求
 * const response = await request({ method: 'GET', url: '/api/users' })
 *
 * // POST 请求
 * const response = await request({
 *   method: 'POST',
 *   url: '/api/users',
 *   data: { name: 'John' }
 * })
 */
export const request = <T = any>(
  config: UnifiedRequestConfig,
): Promise<ApiResponse<T>> => {
  const { method, url, data, ...restConfig } = config;

  switch (method.toUpperCase() as HttpMethod) {
    case "GET":
      return axiosInstance.get<T>(url, restConfig);
    case "POST":
      return axiosInstance.post<T>(url, data, restConfig);
    case "PUT":
      return axiosInstance.put<T>(url, data, restConfig);
    case "DELETE":
      return axiosInstance.delete<T>(url, restConfig);
    case "PATCH":
      return axiosInstance.patch<T>(url, data, restConfig);
    default:
      throw new Error(`不支持的HTTP方法: ${method}`);
  }
};

// 便捷方法（保持向后兼容）
export const get = <T = any>(url: string, config?: RequestConfig) =>
  axiosInstance.get<T>(url, config);

export const post = <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
) => axiosInstance.post<T>(url, data, config);

export const put = <T = any>(url: string, data?: any, config?: RequestConfig) =>
  axiosInstance.put<T>(url, data, config);

export const del = <T = any>(url: string, config?: RequestConfig) =>
  axiosInstance.delete<T>(url, config);

export const patch = <T = any>(
  url: string,
  data?: any,
  config?: RequestConfig,
) => axiosInstance.patch<T>(url, data, config);

// 导出默认实例
export default axiosInstance;
